import {
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import { styled, SxProp } from 'dripsy';
import { observer, useLocalObservable } from 'mobx-react-lite';
import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useModal } from 'react-native-modalfy';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import {
  BaseText,
  Icon,
  Line,
  NavigateButton,
  RefreshControl,
  ScreenLoader,
  ScrollView,
  Separator,
  Touchable,
} from '@components';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { modals, ModalStackParamsType } from '@modals';
import { useAuthModel } from '@models/AuthModel';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { screens, TabNavigatorNavigationPropType } from '@navigation';
import { ToastService } from '@services';
import { IS_WEB, parseApiError } from '@utils';
import {
  ProfileFormGeneral,
  ProfileFormPrivate,
} from '../components';
import { ProfileAvatar } from './components/ProfileAvatar';
import { SectionHeader } from './components/SectionHeader';
import { TextRow } from './components/TextRow';
import { UserStatsView } from './components/UserStatsView';

const ScrollViewContainer = styled(ScrollView)({
  bg: '$background0',
  flex: 1,
});

function contentContainerStyle(props: EdgeInsets): SxProp {
  return {
    pt: [props.top, '$9'],
    px: ['$6', '5%'],
    pb: ['$8', '$12'],
  };
}

function loaderContentContainerStyle(props: EdgeInsets): SxProp {
  return {
    flex: 1,
    ...contentContainerStyle(props),
  };
}

const Text = styled(BaseText)({
  color: '$error300',
  variant: 'text.medium',
  p: '$5',
});

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  py: '$6',
});

const WebTitle = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xbig',
  fontWeight: 'semi',
});

const SectionContainer = styled(View)({
  borderTopWidth: 1,
  borderColor: '$background30',
  py: '$8',
});

const RowContainer = styled(View)({
  flexDirection: ['column', 'row'],
  justifyContent: 'flex-start',
  alignItems: 'center',
  flex: 1,
});

type NavigationPropType = TabNavigatorNavigationPropType<'Profile'>;

export const ProfileScreen = observer(() => {
  const insets = useSafeAreaInsets();
  const nav = useNavigation<NavigationPropType>();
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal<ModalStackParamsType>();
  const isFocused = useIsFocused();
  const viewer = useViewerModel();
  const authModel = useAuthModel();
  const user = viewer.currentUser;

  const store = useLocalObservable(() => ({
    isWebFormGeneral: false,
    isWebFormPrivate: false,

    showGeneralForm() {
      this.isWebFormGeneral = !this.isWebFormGeneral;
    },

    showPrivateForm() {
      this.isWebFormPrivate = !this.isWebFormPrivate;
    },

    hideGeneralForm() {
      this.isWebFormGeneral = false;
    },

    hidePrivateForm() {
      this.isWebFormPrivate = false;
    },

    hideAllForms() {
      this.hideGeneralForm();
      this.hidePrivateForm();
    },
  }));

  useNavOptionsSet({ title: t('profile.title') });

  useEffect(() => {
    // For Web, we need to hide forms when they are opened
    // and screen is not in focus
    if (IS_WEB && !isFocused) {
      store.hideAllForms();
    }
  }, [isFocused]);

  useAsyncInit(async () => {
    try {
      await viewer.fetch.run();
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  });

  async function refresh() {
    try {
      await viewer.refresh.run();
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  }

  function navigateToUpdateGeneral() {
    nav.navigate(screens.ProfileNavigator, {
      screen: screens.UpdateProfileGeneral,
    });
  }

  function navigateToUpdatePrivate() {
    nav.navigate(screens.ProfileNavigator, {
      screen: screens.UpdateProfilePrivate,
    });
  }

  function navigateToSettings() {
    nav.navigate(screens.ProfileNavigator, {
      screen: screens.Settings,
    });
  }

  function openUpdateGeneral() {
    IS_WEB ? store.showGeneralForm() : navigateToUpdateGeneral();
  }

  function openUpdatePrivate() {
    IS_WEB ? store.showPrivateForm() : navigateToUpdatePrivate();
  }

  function logout() {
    authModel.signOut.run();
    authModel.unauthorize();
  }

  function openLogoutModal() {
    openModal(modals.ConfirmationModal, {
      title: t('profile.logout'),
      subtitle: t('profile.logoutSubtitle'),
      buttons: [
        { text: t('profile.logout') },
        { text: t('profile.cancel') },
      ],
      onConfirm: () => {
        logout();
        closeModal();
      },
    });
  }

  if (!user) {
    return (
      <ScrollViewContainer
        contentContainerSx={loaderContentContainerStyle(insets)}
      >
        <ScreenLoader />
        <Line />
        <TouchableContainer onPress={openLogoutModal}>
          <Icon name="logout-light" color="$error300" size={24} />
          <Text>{t('profile.logout')}</Text>
        </TouchableContainer>
      </ScrollViewContainer>
    );
  }

  return (
    <ScrollViewContainer
      contentContainerSx={contentContainerStyle(insets)}
      refreshControl={
        <RefreshControl
          refreshing={viewer.refresh.inProgress}
          onRefresh={refresh}
        />
      }
    >
      {IS_WEB && <WebTitle>{t('profile.title')}</WebTitle>}

      <RowContainer>
        <ProfileAvatar />

        <Separator vertical size="$9" />

        <UserStatsView />
      </RowContainer>

      <Separator size="$6" />

      <SectionContainer>
        <SectionHeader
          text={t('profile.general')}
          onPress={openUpdateGeneral}
          icon="edit-light"
        />
        {store.isWebFormGeneral ? (
          <ProfileFormGeneral hideForm={store.hideGeneralForm} />
        ) : (
          <Fragment>
            <Separator size="$2" />
            <TextRow
              label={t('profile.firstName')}
              details={user.firstName}
            />
            <TextRow
              label={t('profile.lastName')}
              details={user.lastName}
            />
            <TextRow
              label={t('profile.location')}
              details={user.location}
              icon="location-light"
            />
            <TextRow
              label={t('profile.description')}
              details={user.description}
            />
          </Fragment>
        )}
      </SectionContainer>

      <SectionContainer>
        <SectionHeader
          text={t('profile.private')}
          onPress={openUpdatePrivate}
          icon="edit-light"
        />
        {store.isWebFormPrivate ? (
          <ProfileFormPrivate hideForm={store.hidePrivateForm} />
        ) : (
          <Fragment>
            <Separator size="$2" />
            <TextRow
              label={t('profile.phone')}
              details={user.phone}
            />
            <TextRow
              label={t('profile.email')}
              details={user.email}
            />
          </Fragment>
        )}
      </SectionContainer>

      <Line />
      <NavigateButton
        onPress={navigateToSettings}
        text={t('profile.settings')}
      />

      <Line />
      <TouchableContainer onPress={openLogoutModal}>
        <Icon name="logout-light" color="$error300" size={24} />
        <Text>{t('profile.logout')}</Text>
      </TouchableContainer>
    </ScrollViewContainer>
  );
});
